<template>
    <!-- Admin: what customers on this server can use. The feature list and what each server type supports
         come from the backend (featureTable), so a new feature or server type needs no change here. -->
    <div class="features-box">
        <p class="features-box__title">Features</p>
        <p class="features-box__hint">What this server offers customers. Off = removed from the package cards and from My Proxy.</p>
        <div class="features-box__list">
            <label v-for="f in list" :key="f.key" class="features-box__item" :class="{ 'is-off': !f.on, 'is-na': !f.supported }">
                <input type="checkbox" class="features-box__input" :checked="f.on" :disabled="!f.supported" @change="toggle(f.key, $event.target.checked)" />
                <span class="features-box__switch" aria-hidden="true"></span>
                <span class="features-box__text">
                    <span class="features-box__name">{{ f.label }}</span>
                    <span class="features-box__state">{{ !f.supported ? 'Not available on this server type' : f.on ? 'On' : 'Off' }}</span>
                </span>
            </label>
        </div>
        <label class="features-box__speed">
            <span class="features-box__name">Max speed shown to customers</span>
            <span class="features-box__speedrow">
                <input type="number" min="1" step="1" placeholder="No limit" class="form-control shadow-none rounded-0 text-black features-box__speedinput"
                    :value="maxSpeed ?? ''" @input="emit('update:maxSpeed', $event.target.value === '' ? null : Number($event.target.value))" />
                <span>Mbps</span>
            </span>
            <span class="features-box__state">Shown as "Up to 10 Mbps" on the carrier tile, the package cards and the purchase window. A label only: set the real cap on the server itself. Leave empty for no limit.</span>
        </label>
    </div>
</template>

<script setup>
import { computed, defineEmits, defineProps } from 'vue';
import { storeToRefs } from 'pinia';
import { useAdminServerStore } from '@/stores';

const props = defineProps({
    modelValue: { type: Object, default: () => ({}) },   // { openvpn, wireguard, os_spoofing, udp }
    serverType: { type: String, default: '' },           // X_PROXY / XPROXY_NEW / PROXYSMART / a future type
    maxSpeed: { type: Number, default: null },           // Mbps, null = no cap shown
});
const emit = defineEmits(['update:modelValue', 'update:maxSpeed']);
const { featureTable } = storeToRefs(useAdminServerStore());

const list = computed(() => (featureTable.value?.features || []).map((f) => {
    const row = featureTable.value?.support?.[props.serverType];
    const supported = row ? row[f.key] === true : true;   // a type with no row supports everything
    const chosen = typeof props.modelValue?.[f.key] === 'boolean' ? props.modelValue[f.key] : f.defaultOn;
    return { ...f, supported, on: supported && chosen };
}));
const toggle = (key, value) => emit('update:modelValue', { ...props.modelValue, [key]: value });
</script>

<style scoped>
.features-box { margin: 4px 8px 12px; padding: 12px; border: 1px solid var(--ui-line-strong); border-radius: 10px; clear: both; }
.features-box__title { margin: 0 0 2px; font-weight: 600; }
.features-box__hint { margin: 0 0 10px; font-size: 12px; color: var(--ui-dim); }
.features-box__list { display: flex; flex-wrap: wrap; gap: 8px; }
.features-box__item { position: relative; display: flex; align-items: center; gap: 10px; flex: 1; min-width: 150px; margin: 0; padding: 8px 10px; border: 1px solid var(--ui-line); border-radius: 10px; background: var(--ui-card); cursor: pointer; }
.features-box__item.is-off { opacity: 0.6; }
.features-box__item.is-na { opacity: 0.45; cursor: not-allowed; }
.features-box__input { position: absolute; opacity: 0; width: 0; height: 0; }
.features-box__switch { position: relative; flex: none; width: 34px; height: 20px; border-radius: 999px; background: var(--ui-line-strong); transition: background-color 150ms ease; }
.features-box__switch::after { content: ""; position: absolute; top: 2px; left: 2px; width: 16px; height: 16px; border-radius: 50%; background: #ffffff; transition: left 150ms ease; }
.features-box__input:checked + .features-box__switch { background: var(--ui-pri); }
.features-box__input:checked + .features-box__switch::after { left: 16px; }
.features-box__input:focus-visible + .features-box__switch { outline: 2px solid var(--ui-pri-hover); outline-offset: 2px; }
.features-box__speed { display: flex; flex-direction: column; gap: 4px; margin: 12px 0 0; }
.features-box__speedrow { display: flex; align-items: center; gap: 8px; }
.features-box__speedinput { width: 140px; }
.features-box__text { display: flex; flex-direction: column; line-height: 1.25; }
.features-box__name { font-weight: 600; font-size: 13.5px; }
.features-box__state { font-size: 11.5px; color: var(--ui-dim); }
</style>
